import React, { useState } from 'react'
import { X, AlertTriangle, Calendar, FileText } from 'lucide-react'

interface ModalInativarContratoProps {
  contrato: {
    id: string
    numero_contrato: string
    status: string
  }
  isOpen: boolean
  onClose: () => void
  onConfirm: (contratoId: string, dataEncerramento: string, motivoEncerramento: string) => void
}

const ModalInativarContrato: React.FC<ModalInativarContratoProps> = ({
  contrato,
  isOpen,
  onClose,
  onConfirm
}) => {
  const [dataEncerramento, setDataEncerramento] = useState(
    new Date().toISOString().split('T')[0]
  )
  const [motivoEncerramento, setMotivoEncerramento] = useState('')
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!motivoEncerramento.trim()) {
      alert('Por favor, informe o motivo do encerramento')
      return
    }

    setLoading(true)
    try {
      await onConfirm(contrato.id, dataEncerramento, motivoEncerramento)
      onClose()
    } catch (error) {
      console.error('Erro ao inativar contrato:', error)
    } finally {
      setLoading(false)
    }
  }

  const isInativar = contrato.status === 'ativo'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className={`p-6 rounded-t-2xl ${isInativar ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-green-500 to-emerald-500'} text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-xl">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {isInativar ? 'Inativar Contrato' : 'Reativar Contrato'}
                </h2>
                <p className="text-sm opacity-90">
                  Contrato: {contrato.numero_contrato}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {isInativar ? (
            <>
              {/* Data de Encerramento */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span>Data de Encerramento *</span>
                </label>
                <input
                  type="date"
                  value={dataEncerramento}
                  onChange={(e) => setDataEncerramento(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              {/* Motivo do Encerramento */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-4 h-4" />
                  <span>Motivo do Encerramento *</span>
                </label>
                <textarea
                  value={motivoEncerramento}
                  onChange={(e) => setMotivoEncerramento(e.target.value)}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Descreva o motivo do encerramento do contrato..."
                />
              </div>

              {/* Aviso */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold mb-1">Atenção!</p>
                    <p>
                      Ao inativar este contrato, ele será marcado como encerrado e não aparecerá mais como ativo nos relatórios. 
                      Esta ação pode ser revertida posteriormente se necessário.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-800">
                  <p className="font-semibold mb-1">Reativar Contrato</p>
                  <p>
                    Ao reativar este contrato, ele voltará a aparecer como ativo nos relatórios.
                    Os dados de encerramento serão removidos.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-semibold transition-all duration-200 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-3 rounded-xl text-white font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 ${
                isInativar
                  ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600'
                  : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
              }`}
            >
              {loading ? 'Processando...' : isInativar ? 'Inativar Contrato' : 'Reativar Contrato'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ModalInativarContrato
